import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import invalidateCache from 'utils/api/invalidateCache';
import PlanTab from 'views/AddNewServices/AddSingleService/tabs/PlanTab';
import NumberTab from 'views/AddNewServices/AddSingleService/tabs/NumberTab';
import SimTab from 'views/AddNewServices/AddSingleService/tabs/SimTab';
import SummaryTab from 'views/AddNewServices/AddSingleService/tabs/SummaryTab';
import ServicesTab from 'views/AddNewServices/AddSingleService/tabs/ServicesTab';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { ProductsService } from 'services/products/products';
import { NumbersService } from 'services/numbers/numbers';
import { ResourcesService } from 'services/resources/resources';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { catchError, concatMap, finalize } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { setToast } from 'redux/modules/toast/actions';
import { translate } from 'components/Translation';
import { DEFAULT_PLACE, serviceType } from 'views/AddNewServices/AddSingleService/constants';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import AddNewServiceWrapper from 'views/AddNewServices/AddSingleService/common/AddNewServiceWrapper';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/addLineDialogStyle';
import { makeStyles } from '@material-ui/core';
import { debounce } from 'lodash';
import BillingAccountTab from '../tabs/BillingAccountTab';
import { BasicPlan, Commitments, ProductsDetailsResponse } from 'utils/api/models/postpaidLines/postpaidLines';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import OTP from 'components/OTP/OTP';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { UsersService } from 'services/users/users';
import { sessionSelector } from 'redux/modules/session/selectors';
import OtpDialog from 'views/Auth/common/OtpDialog';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);

const steps = {
	NUMBER: 0,
	BILLING_ACCOUNT: 1,
	SIM: 2,
	PLAN: 3,
	SERVICES: 4,
	SUMMARY: 5,
};

const orderItems: types.newServices.IOrderItem[] = [
	{
		legacyProductId: '2001',
		place: 'OM',
	},
	{
		legacyProductId: '2003',
		place: 'OM',
	},
];

const simTypes = {
	SIMCard: 'SIM',
	eSIM: 'ESIM',
};

interface IMobileServiceProps {
	account: IAlternativeSelectItem | null;
	billingAccounts: SingleBillId[];
	users: SingleUser[];
	back: () => void;
	billingAccountTypes: string[];
	availableServices: number[];
	showConfirmationDialog: (selectedNumber: string, simDetails?: types.newServices.ISimDetails) => void;
}

const MobileService = ({
	account,
	billingAccounts,
	users,
	billingAccountTypes,
	availableServices,
	back,
	showConfirmationDialog,
}: IMobileServiceProps) => {
	const classes = useStyles();
	const [activeTab, setActiveTab] = useState(steps.NUMBER);
	const [selectedNumber, setSelectedNumber] = useState('');
	const [selectedBillingAccount, setSelectedBillingAccount] = useState<types.newServices.IBillingId | null>(null);
	const [baseNumber, setBaseNumber] = useState<types.newServices.IBaseNumber | null>(null);
	const [serialNumber, setSerialNumber] = useState('');
	const [showSerialNumberError, setShowSerialNumberError] = useState(false);
	const [creditLimit] = useState('');
	const [serialNumberValid, setSerialNumberValid] = useState(false);
	const [selectedOrderItems, setSelectedOrderItems] = useState([...orderItems]);
	const [selectedUser, setSelectedUser] = useState<SingleUser | null>(null);
	const [showServicesTabError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [numbers, setNumbers] = useState<Array<string>>([]);
	const [loadingNumbers, setLoadingNumbers] = useState(true);
	const [helperText, setHelperText] = useState<string | undefined>();
	const [namaBundleCommitmentId, setNamaBundleCommitmentId] = useState<string>('');
	const [asyncValidationInProgress, setAsyncValidationInProgress] = useState<boolean>(false);
	const [searchNumber, setSearchNumber] = useState<string | undefined>();
	const [selectedTariff, setSelectedTariff] = useState<BasicPlan | null>(null);
	const [selectedCommitment, setSelectedCommitment] = useState<Commitments | null>(null);
	const [productsDetails, setProductsDetails] = useState<ProductsDetailsResponse | null>(null);
	const [selectedSimType, setSelectedSimType] = useState<string>(simTypes.eSIM);
	const [commitmentDuration, setCommitmentDuration] = useState<string | null>(null);
	const [activePlanTab, setActivePlanTab] = useState<number>(0);
	const [otp, setOTP] = useState<string>('');
	const [isWrongOtp, setIsWrongOtp] = useState(false);
	const [openOTPDialog, setOpenOTPDialog] = useState(false);
	const user = useSelector(sessionSelector)?.user;
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		setLoadingNumbers(true);
		ResourcesService.getAvailableNumbers('10', searchNumber)
			.pipe(finalize(() => isMounted.current && setLoadingNumbers(false)))
			.subscribe(({ numbers }) => {
				setNumbers(numbers.map(({ msisdn }) => msisdn));
			});
	}, [searchNumber]);

	useEffect(() => {
		if (loading) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	useEffect(() => {
		if (selectedTariff) {
			const tariff: types.newServices.IOrderItem = {
				legacyProductId: selectedTariff.productId,
				place: 'OM',
				name: selectedTariff.productName,
				id: selectedTariff.productId,
				type: serviceType.TARIFF_PLAN,
			};
			setSelectedOrderItems((s) => {
				const previousIndex = s.findIndex((soi) => soi.type === serviceType.TARIFF_PLAN);
				if (previousIndex !== -1) {
					s.splice(previousIndex, 1);
				}
				return [...s, tariff];
			});
		}
	}, [selectedTariff]);

	useEffect(() => {
		if (serialNumber.length !== 19) {
			setShowSerialNumberError(false);
			setHelperText(undefined);
		}
	}, [serialNumber]);

	const selectedMobileInternetPlan = useMemo(() => {
		return selectedOrderItems.find((item) => item.type === serviceType.MOBILE_INTERNET_PLANS) || null;
	}, [selectedOrderItems]);

	const tariffPlan = useMemo(() => {
		return selectedOrderItems.find((item) => item.type === serviceType.TARIFF_PLAN) || null;
	}, [selectedOrderItems]);

	const nextStepDisabled = useMemo(() => {
		switch (activeTab) {
			case steps.BILLING_ACCOUNT:
				return !selectedBillingAccount;

			case steps.NUMBER:
				return !selectedNumber;

			case steps.SIM:
				return selectedSimType === simTypes.SIMCard && serialNumber.length !== 19;

			case steps.PLAN:
				return !selectedTariff;

			case steps.SERVICES:
				return showServicesTabError && !selectedMobileInternetPlan;

			case steps.SUMMARY:
			default:
				return false;
		}
	}, [
		activeTab,
		selectedBillingAccount,
		serialNumber,
		selectedNumber,
		selectedMobileInternetPlan,
		showServicesTabError,
		selectedTariff,
		selectedSimType,
	]);

	const filteredBillingAccounts = useMemo(() => {
		return billingAccounts.filter(
			(billingAccount) => billingAccountTypes.indexOf(billingAccount.billingAccountType) >= 0
		);
	}, [billingAccounts, billingAccountTypes]);

	const setTariffPlan = useCallback(
		(plan: any) => {
			setSelectedOrderItems((selectedOrderItems) => {
				const _selectedOrderItems = selectedOrderItems.filter((item) => item.type !== serviceType.TARIFF_PLAN);
				if (plan) {
					_selectedOrderItems.push({
						legacyProductId: plan.legacyProductId,
						place: DEFAULT_PLACE,
						name: plan.name,
						type: serviceType.TARIFF_PLAN,
						id: plan.productId,
					});
				}
				return _selectedOrderItems;
			});
		},
		[setSelectedOrderItems]
	);

	const nextStepAction = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case steps.SUMMARY:
					requestForOtp();
					return activeTab;

				case steps.SIM:
					setShowSerialNumberError(false);
					if (selectedSimType === simTypes.SIMCard) {
						return serialNumberValid ? steps.PLAN : steps.SIM;
					}
					return steps.PLAN;

				default:
					return activeTab + 1;
			}
		});
	};

	const cancelAction = () => {
		if (activeTab === steps.NUMBER) {
			back();
		} else {
			setActiveTab((value) => value - 1);
		}
	};

	const onSuccess = (simDetails?: types.newServices.ISimDetails) => {
		invalidateCache(['getNumbersForAccount'], true);
		back();
		showConfirmationDialog(selectedNumber, simDetails);
	};

	const addServices = () => {
		const selectedServices = selectedOrderItems.map((item) => {
			if (item.type === serviceType.GSM) item.legacyProductId = item.legacyProductId.replace('OM_', '');
			return item.id ? item.id : item.legacyProductId;
		});
		if (selectedCommitment) selectedServices.push(selectedCommitment.productId);
		return selectedServices;
	};

	const parseQrCode = (data: string) => {
		return data.substring(10, data.length - 4);
	};

	const addNumber = () => {
		if (selectedBillingAccount) {
			setLoading(true);
			const getUsageResponse = ProductsService.submitProductOrder({
				accountId: selectedBillingAccount.billingId,
				msisdn: selectedNumber,
				billingType: 'POSTPAID',
				externalAttributes: {
					MSISDN_POSTPAID: selectedNumber,
					SERIAL_POSTPAID: serialNumber,
				},
				services: addServices(),
				simTypeEnum: selectedSimType,
				otp,
			});

			const assignNumberResponse = getUsageResponse.pipe(
				concatMap(({ response }: any) => {
					if (response) {
						setLoading(false);
						setOpenOTPDialog(false);
						if (response.result === false) {
							dispatch(
								setToast({
									error: true,
									label: translate('error.generic-title'),
									actionType: translate('error'),
								})
							);
							return EMPTY;
						}
						if (selectedUser) {
							return NumbersService.assignNumbersForCorpUserRestPost({
								userId: selectedUser.userId,
								numbers: [selectedNumber],
							});
						}
						if (response.simDetails && response.type === 'ESIM')
							response.simDetails.qrCode = parseQrCode(response.simDetails.qrCode);
						onSuccess(response.simDetails);
						return EMPTY;
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('error.invalid.request'),
								actionType: translate('error'),
							})
						);

						setLoading(false);
						return EMPTY;
					}
				}),
				catchError((error) => {
					setLoading(false);
					if (error.response.errorCode === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
						setOpenOTPDialog(true);
						return;
					}
					throwError(error);

					return error;
				})
			);

			assignNumberResponse.pipe(finalize(() => setLoading(false))).subscribe(() => {
				onSuccess();
			});
		}
	};

	const validateSerialNumber = () => {
		if (selectedSimType === simTypes.SIMCard) {
			setAsyncValidationInProgress(true);
			setShowSerialNumberError(false);
			setHelperText(undefined);
			ProductsService.checkIfResourceIsAvailable(serialNumber).subscribe(
				({ response }) => {
					setAsyncValidationInProgress(false);
					if (response) {
						invalidateCache(['checkResourceAvailable'], true);
						setSerialNumberValid(true);
						nextStepAction();
					} else {
						setWrongNumber();
					}
				},
				() => {
					setWrongNumber();
				}
			);
		} else {
			nextStepAction();
		}
	};

	const setWrongNumber = () => {
		invalidateCache(['checkResourceAvailable'], true);
		setAsyncValidationInProgress(false);
		setSerialNumberValid(false);
		setShowSerialNumberError(true);
		setHelperText(translate('numbers.settings.replace.sim.card.invalid'));
	};

	const setSearch = useCallback(
		debounce((event: string) => {
			setSearchNumber(event);
		}, 300),
		[]
	);

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		UsersService.requestForOtp(OperationContextEnum.ADDPOSTPAID, user.contactNumber1)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
				setOpenOTPDialog(true);
			});
	};

	return (
		<>
			<AddNewServiceWrapper
				tabs={[
					{
						name: translate('add.line.number'),
						title: translate('add.line.number'),
						content: (
							<NumberTab
								numbers={numbers}
								setSelectedNumber={setSelectedNumber}
								selectedNumber={selectedNumber}
								users={users}
								selectedUser={selectedUser}
								setSelectedUser={setSelectedUser}
								loading={loadingNumbers}
								setSearch={setSearch}
							/>
						),
					},
					{
						name: translate('add.line.billing.account'),
						title: translate('add.line.billing.account'),
						content: (
							<BillingAccountTab
								billingAccounts={filteredBillingAccounts}
								selectedBillingAccount={selectedBillingAccount}
								setSelectedBillingAccount={setSelectedBillingAccount}
							/>
						),
					},
					{
						name: translate('add.line.sim'),
						title: translate('add.line.sim'),
						content: (
							<>
								<ConditionalRender
									show={asyncValidationInProgress}
									onTrue={() => (
										<div className={classes.circularProgressContainer}>
											<CustomCircularProgress
												loading
												text={translate('numbers.settings.replace.sim.card.validation.message')}
												whiteBackground={true}
											/>
										</div>
									)}
								/>
								<SimTab
									showError={showSerialNumberError}
									serialNumber={serialNumber}
									helperText={helperText}
									setSerialNumber={setSerialNumber}
									setSerialNumberValid={setSerialNumberValid}
									selectedSimType={selectedSimType}
									setSelectedSimType={setSelectedSimType}
									simTypes={[simTypes.eSIM, simTypes.SIMCard]}
								/>
							</>
						),
					},
					{
						name: translate('add.line.plan'),
						title: translate('add.line.plan'),
						content: (
							<PlanTab
								billingAccounts={filteredBillingAccounts}
								selectedBillingAccount={selectedBillingAccount}
								setSelectedBillingAccount={setSelectedBillingAccount}
								setTariffPlan={setTariffPlan}
								baseNumber={baseNumber}
								setBaseNumber={setBaseNumber}
								setNamaBundleCommitmentId={setNamaBundleCommitmentId}
								setSelectedTariff={setSelectedTariff}
								setSelectedCommitment={setSelectedCommitment}
								setProductsDetails={setProductsDetails}
								setCommitmentDuration={setCommitmentDuration}
								commitmentDuration={commitmentDuration}
								selectedTariff={selectedTariff}
								activeTab={activePlanTab}
								setActiveTab={setActivePlanTab}
							/>
						),
					},
					{
						name: translate('add.line.services'),
						title: translate('add.line.services'),
						content: (
							<ServicesTab
								selectedOrderItems={selectedOrderItems}
								setSelectedOrderItems={setSelectedOrderItems}
								showError={showServicesTabError}
								baseNumber={baseNumber}
								availableServices={availableServices}
								namaBundleCommitmentId={namaBundleCommitmentId}
								tariffPlanId={tariffPlan?.id || ''}
								productsDetails={productsDetails}
								selectedTariff={selectedTariff}
							/>
						),
					},
					{
						name: translate('summary'),
						title: translate('summary'),
						content: (
							<SummaryTab
								account={account}
								selectedBillingAccount={selectedBillingAccount}
								selectedNumber={selectedNumber}
								tariffPlan={tariffPlan}
								creditLimit={creditLimit}
								selectedOrderItems={selectedOrderItems}
								selectedUser={selectedUser}
								availableServices={availableServices}
								commitmentDuration={commitmentDuration}
								isRoamingInSelectedTariff={selectedTariff?.roaming}
							/>
						),
					},
				]}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				buttons={[
					{
						text: activeTab === steps.NUMBER ? translate('cancel') : translate('back'),
						onClick: cancelAction,
						color: 'tertiary-light' as types.theme.buttonColor,
					},
					{
						text: translate('next'),
						onClick: activeTab !== steps.SIM ? nextStepAction : validateSerialNumber,
						color: 'primary-light' as types.theme.buttonColor,
						disabled: nextStepDisabled || loading,
						id: 'next-button',
					},
				]}
			/>
			{openOTPDialog && (
				<OtpDialog
					heading={translate('enter.otp.title')}
					isOpen={openOTPDialog}
					otp={otp}
					setOtp={setOTP}
					isWrongOtp={isWrongOtp}
					setIsWrongOtp={setIsWrongOtp}
					onClose={() => {
						setOpenOTPDialog(false);
					}}
					onSubmit={addNumber}
					submitText={translate('add.line')}
					sendOtp={requestForOtp}
					loading={loading}
				/>
			)}
		</>
	);
};

export default MobileService;
